import gql from 'graphql-tag';
import { EventCoreAttributesWithContext, buildEventFragment } from './events';
import { ErrorsAttributes } from '../constants';
import { LabelFragment } from './labels';

export const UserCoreAttributes = `
id
name
email
signInCount
isSuperUser
otpRequiredForLogin
usesPassword
avatarUrl
avatarColor
`;

export const MembershipCoreAttributes = `
id
role
ssoId
issuesFilter
invitationAcceptedAt
invitationPending
invitationSentAt
account {
  id
  name
  customDomain {
    id
    name
    usesSsoId
    singleSignOnRequired
  }
}
`;

export const UserMembershipCoreAttributes = `
memberships {
  ${MembershipCoreAttributes}
}
currentMembership {
  ${MembershipCoreAttributes}
}
currentRole
agencyMembership {
  id
  role
  agency {
    id
    name
  }
}
groupMemberships {
  id
  role
  group {
    id
    name
    allSites
    sites {
      id
      name
    }
  }
}
siteMemberships {
  id
  role
  site {
    id
    name
  }
}
`;

const UserNotificationAttributes = `
crawlerFinishedNotificationSetting
crawlerFinishedNotificationSites {
  id
  name
  labels {
    ...LabelFragment
  }
}
groupScoreNotificationSetting
groupScoreNotificationGroups {
  id
  name
  labels {
    ...LabelFragment
  }
}
settings {
  key
  name
  value
}
`;

const CurrentUserAttributes = `
  ${UserCoreAttributes}
  hasGithubAccessToken
  githubRepository
  githubClientId
  hasGitlabAccessToken
  gitlabRepository
  gitlabClientId
  hasJiraAccessToken
  jiraProject
  jiraClientId
  ${UserNotificationAttributes}
`;

export const PinnedItemFragment = gql`
  fragment PinnedItemFragment on PinnedItem {
    id
    createdAt
    thingId
    thingType
  }
`;

export const CurrentUserQueryForHook = gql`
  query CurrentUserQueryForHook($accountId: String, $thingType: PinnedThingType) {
    currentUser(accountId: $accountId) {
      ${CurrentUserAttributes}
      pinnedItems(thingType: $thingType) {
        ...PinnedItemFragment
      }
    }
  }
  ${PinnedItemFragment}
  ${LabelFragment}
`;

export const CurrentUserApiKeyOnlyQuery = gql`
  query CurrentUserApiKeyOnlyQuery($accountId: String) {
    currentUser(accountId: $accountId) {
      apiKey
    }
  }
`;

export const CurrentUserForRedirectQuery = gql`
  query CurrentUserForRedirectQuery {
    currentUser(accountId: null) {
      isSuperUser
      memberships {
        account {
          id
          name
          usesSingleSignOut
          customDomain {
            id
            name
            usesSsoId
            singleSignOnRequired
          }
        }
      }
    }
  }
`;

export const CurrentUserUnreadEventsCountQuery = gql`
  query CurrentUserUnreadEventsCountQuery($accountId: String!) {
    currentUser(accountId: $accountId) {
      id
      unreadEventsCount(accountId: $accountId)
    }
  }
`;

export const CurrentUserNotificationsQuery = gql`
  query CurrentUserNotificationsQuery(
    $accountId: String!
    $page: Int
    $perPage: Int
    $eventTypes: [String!]
    $timeframe: Timeframe
    $timeframeStart: Date
    $timeframeEnd: Date
    $newOnly: Boolean
    $markViewed: Boolean
  ) {
    currentUser(accountId: $accountId) {
      id
      notifications(
        accountId: $accountId
        page: $page
        perPage: $perPage
        eventTypes: $eventTypes
        timeframe: $timeframe
        timeframeStart: $timeframeStart
        timeframeEnd: $timeframeEnd
        newOnly: $newOnly
        markViewed: $markViewed
      ) {
        currentPage
        perPage
        totalPages
        totalEntries
        nodes {
          ...NotificationEventFragment
        }
      }
    }
  }
  ${buildEventFragment(EventCoreAttributesWithContext, 'NotificationEventFragment')}
`;

export const UserQuery = gql`
  query UserQuery($userId: String!, $accountId: String!) {
    user(userId: $userId, accountId: $accountId) {
      ${UserCoreAttributes}
      ${UserMembershipCoreAttributes}
      ${UserNotificationAttributes}
      unconfirmedEmail
    }
  }
  ${LabelFragment}
`;

export const InviteUserMutation = gql`
  mutation InviteUser($accountId: String!, $name: String!, $email: String!, $role: Role!, $ssoId: String, $issuesFilter: UserIssuesFilter, $groupIds: [String!], $groupMemberships: [GroupMembershipInput!], $siteMemberships: [SiteMembershipInput!]) {
    inviteUser(accountId: $accountId, name: $name, email: $email, role: $role, ssoId: $ssoId, issuesFilter: $issuesFilter, groupIds: $groupIds, groupMemberships: $groupMemberships, siteMemberships: $siteMemberships) {
      membership {
        ${MembershipCoreAttributes}
        invitedBy {
          ${UserCoreAttributes}
        }
        user {
          ${UserCoreAttributes}
        }
      }
    }
  }
`;

export const UpdateUserMutation = gql`
  mutation UpdateUser($accountId: String!, $userId: String!, $name: String, $email: String, $role: Role, $ssoId: String, $issuesFilter: UserIssuesFilter, $avatarColor: String, $avatarImageData: String, $crawlerFinishedNotificationSetting: UserCrawlerFinishedNotificationSetting, $crawlerFinishedNotificationSiteIds: [String!], $groupScoreNotificationSetting: UserGroupScoreNotificationSetting, $groupScoreNotificationGroupIds: [String!], $settings: [UserSettingInput!], $groupIds: [String!], $groupMemberships: [GroupMembershipInput!], $siteMemberships: [SiteMembershipInput!]) {
    updateUser(accountId: $accountId, userId: $userId, name: $name, email: $email, role: $role, ssoId: $ssoId, issuesFilter: $issuesFilter, avatarColor: $avatarColor, avatarImageData: $avatarImageData, crawlerFinishedNotificationSetting: $crawlerFinishedNotificationSetting, crawlerFinishedNotificationSiteIds: $crawlerFinishedNotificationSiteIds, groupScoreNotificationSetting: $groupScoreNotificationSetting, groupScoreNotificationGroupIds: $groupScoreNotificationGroupIds, settings: $settings, groupIds: $groupIds, groupMemberships: $groupMemberships, siteMemberships: $siteMemberships) {
      user {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const UsersQuery = gql`
  query UsersQuery($accountId: String!, $siteId: String, $page: Int!, $perPage: Int!, $sortBy: UsersSortBy!, $sortOrder: SortOrder!, $search: String, $filters: UsersFiltersInput) {
    users(accountId: $accountId, siteId: $siteId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, filters: $filters) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        unconfirmedEmail
        currentSignInAt
        lastSignInAt
        createdAt
      }
    }
  }
`;

export const UsersQueryForSelection = gql`
  query UsersQueryForSelection($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: UsersSortBy!, $sortOrder: SortOrder!, $search: String) {
    users(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${UserCoreAttributes}
        createdAt
      }
    }
  }
`;

export const RemoveUsersMutation = gql`
  mutation RemoveUsers($accountId: String!, $userIds: [String!]!) {
    removeUsers(accountId: $accountId, userIds: $userIds) {
      status
    }
  }
`;

export const ChangeUserPasswordMutation = gql`
  mutation ChangeUserPassword($currentPassword: String!, $newPassword: String!, $newPasswordConfirmation: String!) {
    changeUserPassword(currentPassword: $currentPassword, newPassword: $newPassword, newPasswordConfirmation: $newPasswordConfirmation) {
      user {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const NewOneTimePasswordMutation = gql`
  mutation newOneTimePassword {
    newOneTimePassword {
      otpProvisioningUri
    }
  }
`;

export const EnableOneTimePasswordMutation = gql`
  mutation enableOneTimePassword($otpAttempt: String!) {
    enableOneTimePassword(otpAttempt: $otpAttempt) {
      backupCodes
      user {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const DisableOneTimePasswordMutation = gql`
  mutation disableeOneTimePassword($password: String!) {
    disableOneTimePassword(password: $password) {
      user {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const ResendUserInvitationMutation = gql`
  mutation ResendUserInvitation($accountId: String!, $userId: String!) {
    resendUserInvitation(accountId: $accountId, userId: $userId) {
      user {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const CancelUserEmailChangeMutation = gql`
  mutation CancelUserEmailChangeMutation($accountId: String!, $userId: String!) {
    cancelUserEmailChange(accountId: $accountId, userId: $userId) {
      user {
        ${UserCoreAttributes}
        ${UserMembershipCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const RegenerateApiKeyMutation = gql`
  mutation RegenerateApiKey {
    regenerateApiKey {
      currentUser {
        apiKey
      }
    }
  }
`;

export const RemoveApiKeyMutation = gql`
  mutation RemoveApiKey {
    removeApiKey {
      currentUser {
        apiKey
      }
    }
  }
`;
export const userStateDefaults = {
  name: '',
  email: '',
  groupMemberships: [],
  memberships: [],
  role: 'viewer',
  issuesFilter: 'no_issues_filter',
};

export const determineUserRole = (user, accountId) => {
  let role = userStateDefaults.role;

  const membership = getUserMembership(user, accountId);
  if (membership && membership.role) {
    role = membership.role;
  }

  return role;
};

export const getUserMembership = (user, accountId) => {
  if (user && user.memberships && user.memberships.length) {
    const membership = user.memberships.find(m => m.account.id === accountId);
    return membership;
  }
}

export const userRoles = [
  { value: 'none', label: 'Determined at Site/Page Set Level' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' }
];

export const userIssuesFilters = [
  { value: 'no_issues_filter', label: 'See all issues' },
  { value: 'content_editor', label: 'See issues relevant to content editors' }
];

export const userIssuesFilterValues = [
  'no_issues_filter',
  'content_editor'
];

export function findRole(value) {
  return userRoles.find(r => r.value === value);
}

export const userSettingsToSimpleArray = (userSettings) => {
  return Object.values(userSettings).map(s => ({ key: s.key, value: s.value }));
};

export const mapUserSettingsById = (userSettings) => {
    const mappedById = {};
    (userSettings || []).forEach(s => mappedById[s.key] = s);
    return mappedById;
};

export const buildNotificationSettingsState = ({
  settings,
  crawlerFinishedNotificationSetting,
  crawlerFinishedNotificationSites,
  groupScoreNotificationSetting,
  groupScoreNotificationGroups,
} = {}) => {
  const sites = crawlerFinishedNotificationSites || [];
  const groups = groupScoreNotificationGroups || [];
  return {
    settings: Array.isArray(settings) ? mapUserSettingsById(settings) : (settings || {}),
    crawlerFinishedNotificationSetting,
    crawlerFinishedNotificationSites: sites,
    notificationFilter: sites.length ? 'selected_sites' : 'all',
    groupScoreNotificationGroups: groups,
    groupScoreNotificationSetting: groupScoreNotificationSetting || (groups.length ? 'notify_via_weekly_group_score_digest' : 'never_notify_group_score_digest'),
  };
};

export const notificationSettingsToMutationPayload = (state) => ({
  settings: userSettingsToSimpleArray(state.settings || {}),
  crawlerFinishedNotificationSetting: state.crawlerFinishedNotificationSetting,
  crawlerFinishedNotificationSiteIds: state.notificationFilter === "all" ? [] : (state.crawlerFinishedNotificationSites || []).map(s => s.id),
  groupScoreNotificationSetting: state.groupScoreNotificationSetting,
  groupScoreNotificationGroupIds: state.groupScoreNotificationSetting === "never_notify_group_score_digest" ? [] : (state.groupScoreNotificationGroups || []).map(g => g.id),
});
